import {
    Heading,
    Text,
    useTheme,
    View,
    useAuthenticator,
    Button,
    TextField,
    PasswordField,
    CheckboxField,
    Divider,
} from "@aws-amplify/ui-react"
import { Key, Lock, Smartphone } from "@mui/icons-material"
import { Box, Link, Typography } from "@mui/material"
import { ElementType, useEffect, useRef, useState } from "react"

import UnableToLoginHelp from "./unableToLoginHelp"
import MFAExample from "../assets/images/mfa-code-example.png"
import { PRIVACY_POLICY, TERMS_OF_USE } from "../router/routes"

interface AuthComponentsOptions {
    onShowMfaHelp: () => void
}

interface MfaHelpStep {
    title: string
    description: string
    Icon: ElementType
}

const mfaHelpSteps: MfaHelpStep[] = [
    {
        title: "1. Open your authenticator app",
        description:
            "Open the authenticator app you used to scan the QR code during account setup.",
        Icon: Smartphone,
    },
    {
        title: "2. Find your account",
        description: "Locate the Connect Direct account.",
        Icon: Key,
    },
    {
        title: "3. Enter the 6-digit code",
        description: "Enter the current 6-digit code displayed in the app.",
        Icon: Lock,
    },
]

export const mfaGuide = ({
    onShowMfaHelp,
}: AuthComponentsOptions): Record<string, unknown> => ({
    Header() {
        const { tokens } = useTheme()

        return <View textAlign="center" padding={tokens.space.large} />
    },

    SignIn: {
        Header() {
            const { tokens } = useTheme()

            return (
                <Heading
                    padding={`${tokens.space.medium} 0 0 0`}
                    level={3}
                    textAlign="center"
                >
                    Sign In
                </Heading>
            )
        },
        Footer() {
            const { toForgotPassword } = useAuthenticator()
            const { tokens } = useTheme()

            return (
                <View
                    textAlign="center"
                    padding={`0 0 ${tokens.space.medium} 0`}
                >
                    <Button
                        fontWeight="normal"
                        onClick={toForgotPassword}
                        size="small"
                        variation="link"
                    >
                        Reset Password
                    </Button>
                </View>
            )
        },
    },

    SignUp: {
        Header() {
            const { tokens } = useTheme()

            return (
                <Heading
                    padding={`${tokens.space.medium} 0 0 0`}
                    level={3}
                    textAlign="center"
                >
                    Create a new account
                </Heading>
            )
        },
        Footer() {
            const { toSignIn } = useAuthenticator()
            const { tokens } = useTheme()

            return (
                <>
                    <View
                        paddingLeft={32}
                        paddingRight={32}
                        paddingBottom={32}
                        paddingTop={12}
                    >
                        <Typography variant="body1">
                            <Link href={PRIVACY_POLICY} target="_blank">
                                <Divider label="Privacy policy" />
                            </Link>
                        </Typography>
                    </View>

                    <View
                        textAlign="center"
                        padding={`0 0 ${tokens.space.medium} 0`}
                    >
                        <Button
                            fontWeight="normal"
                            onClick={toSignIn}
                            size="small"
                            variation="link"
                        >
                            Back to Sign In
                        </Button>
                    </View>
                </>
            )
        },
        FormFields() {
            const { validationErrors } = useAuthenticator()

            return (
                <>
                    <TextField
                        name="given_name"
                        label="First Name"
                        isRequired
                        placeholder="Enter your first name"
                        hasError={!!validationErrors.given_name}
                        errorMessage={validationErrors.given_name as string}
                    />
                    <TextField
                        name="family_name"
                        label="Surname"
                        placeholder="Enter your surname name"
                        isRequired
                        hasError={!!validationErrors.family_name}
                        errorMessage={validationErrors.family_name as string}
                    />
                    <TextField
                        name="username"
                        label="Email"
                        placeholder="Enter your email address"
                        isRequired
                        hasError={!!validationErrors.username}
                        errorMessage={validationErrors.username as string}
                    />
                    <PasswordField
                        name="password"
                        label="Password"
                        placeholder="Enter your password"
                        isRequired
                        hasError={!!validationErrors?.password}
                        errorMessage={
                            Array.isArray(validationErrors?.password)
                                ? validationErrors?.password.join(". ")
                                : validationErrors?.password
                        }
                    />
                    <PasswordField
                        name="confirm_password"
                        label="Confirm Password"
                        isRequired
                        placeholder="Please confirm your password"
                        hasError={!!validationErrors?.confirm_password}
                        errorMessage={
                            Array.isArray(validationErrors?.confirm_password)
                                ? validationErrors?.confirm_password.join(". ")
                                : validationErrors?.confirm_password
                        }
                    />
                    <CheckboxField
                        isRequired
                        name="acknowledgement"
                        value="yes"
                        textAlign="center"
                        margin="12px 0px"
                        label={
                            <Typography variant="body1">
                                I have read and accept our{" "}
                                <Link href={TERMS_OF_USE} target="_blank">
                                    Terms of Use
                                </Link>
                            </Typography>
                        }
                    />
                </>
            )
        },
    },
    ConfirmSignUp: {
        Header() {
            const { tokens } = useTheme()
            return (
                <Heading
                    padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                    level={3}
                >
                    Confirm Email Address
                </Heading>
            )
        },
    },
    SetupTotp: {
        Header() {
            const { tokens } = useTheme()
            return (
                <Heading
                    padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                    level={3}
                >
                    Enter Authenticator App Code:
                </Heading>
            )
        },
    },
    ConfirmSignIn: {
        Header() {
            const { tokens } = useTheme()
            return (
                <Heading
                    padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                    level={3}
                >
                    Enter MFA Code:
                </Heading>
            )
        },
        Footer() {
            const [showHelp, setShowHelp] = useState(false)
            const helpPanelRef = useRef<HTMLDivElement | null>(null)

            useEffect(() => {
                if (showHelp) {
                    onShowMfaHelp()

                    window.requestAnimationFrame(() => {
                        helpPanelRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        })
                    })
                }
            }, [showHelp])

            return (
                <div>
                    {!showHelp && (
                        <View textAlign="center" paddingTop="0.25rem">
                            <Button
                                fontWeight="normal"
                                onClick={() => setShowHelp(true)}
                                size="small"
                                variation="link"
                            >
                                Can&apos;t find your code?
                            </Button>
                        </View>
                    )}

                    {showHelp && (
                        <View className="mfa-help-panel" ref={helpPanelRef}>
                            <Heading
                                padding="0.5rem 0 0 1rem"
                                fontSize="1.2rem"
                                level={3}
                            >
                                How to find my code?
                            </Heading>

                            <Box
                                sx={{
                                    position: "relative",
                                    mt: "1rem",
                                    ml: 1,
                                    pr: 1,
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: "14px",
                                        top: "14px",
                                        bottom: "14px",
                                        width: "2px",
                                        backgroundColor: "#c9d3de",
                                    },
                                }}
                            >
                                {mfaHelpSteps.map(
                                    ({ title, description, Icon }, index) => (
                                        <Box
                                            key={title}
                                            sx={{
                                                display: "flex",
                                                gap: 1.5,
                                                mb:
                                                    index <
                                                    mfaHelpSteps.length - 1
                                                        ? 2.5
                                                        : 0,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: "50%",
                                                    backgroundColor: "#027d95",
                                                    color: "#ffffff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                    zIndex: 1,
                                                }}
                                            >
                                                <Icon
                                                    sx={{ fontSize: "16px" }}
                                                />
                                            </Box>
                                            <Box>
                                                <Text
                                                    fontWeight={700}
                                                    fontSize="0.85rem"
                                                >
                                                    {title}
                                                </Text>
                                                <Text
                                                    fontSize="0.75rem"
                                                    lineHeight="1.7"
                                                >
                                                    {description}
                                                </Text>
                                            </Box>
                                        </Box>
                                    )
                                )}
                            </Box>

                            <View textAlign="center" marginTop="1rem">
                                <img
                                    src={MFAExample}
                                    alt="Authenticator app example"
                                    style={{
                                        width: "320px",
                                        maxWidth: "100%",
                                        height: "auto",
                                    }}
                                />
                            </View>
                        </View>
                    )}

                    <View marginTop="0.75rem">
                        <UnableToLoginHelp />
                    </View>
                </div>
            )
        },
    },
    ForgotPassword: {
        Header() {
            const { tokens } = useTheme()
            return (
                <Heading
                    padding={`0 ${tokens.space.medium} 0 ${tokens.space.medium}`}
                    level={3}
                    textAlign="center"
                >
                    Reset Your Password
                </Heading>
            )
        },
    },
    ConfirmResetPassword: {
        Header() {
            const { tokens } = useTheme()
            return (
                <Heading
                    padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                    level={3}
                >
                    Let&apos;s reset your password!
                </Heading>
            )
        },
    },
})
